import { IntegrationServices } from '@/modules/common/types/integrations';
import { TimeOffRequest } from '@/modules/time-off/types/time-off-request';

export interface ShortProject {
  projectId: number;
  projectName: string;
  logoUrl: string | null;
  colorHex: string;
}

/** The one integration link worth surfacing for a worklog (a worklog only ever syncs from a single source). */
export interface WorklogIntegrationStatus {
  service: IntegrationServices;
  /** False once the link has been soft-deleted (the worklog was edited in Opsium and stopped syncing). */
  isConnected: boolean;
}

export interface TimeTrack {
  id: number;
  date: string;
  trackedMinutes: number;
  description: string;
  isRunning: boolean;
  isApproved?: boolean;
  /** Set when this worklog is/was linked to an integration (e.g. synced from Jira). Absent for manually-created entries. */
  integrations?: WorklogIntegrationStatus;
}

export interface TimeEntryModalData extends Partial<TimeTrack> {
  startTime?: string;
  endTime?: string;
  trackedHours?: number;
  project?: ShortProject;
}

export interface EditableTimeEntry extends TimeEntryModalData {
  id: number;
}

export type TimeTrackRequest = Omit<TimeTrack, 'id' | 'userId' | 'isRunning'>;

export enum TimeOffRequestSummaryStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
}

export interface TimeOffSummaryItem {
  status: TimeOffRequestSummaryStatus;
  totalHours: number;
}

export interface TimeOffDaySummary {
  date: string;
  status: TimeOffRequestSummaryStatus;
  totalHours: number;
}

export interface BookedMinutesBreakdown {
  bookedHours: number;
  isBackgroundWork: boolean;
}

export interface TrackedBreakdown {
  trackedHours: number;
  isBackgroundWork: boolean;
}

export interface ProjectTimeTrackingSummary {
  project: ShortProject;
  trackedHours: number;
  trackedBreakdown: TrackedBreakdown[];
  bookedHours: number; // includes background work
  progressPercentage: number; // (trackedHours / bookedHours) * 100
  bookedBreakdown: BookedMinutesBreakdown[];
}

export interface WeeklySummary {
  weekStartDate: string; // ISO format
  weekEndDate: string; // ISO format
  totalTrackedHours: number;
  trackedBreakdown: TrackedBreakdown[];
  totalBookedHours: number; // includes background work
  /** Raw working capacity hours for the user in the given period (before any time-off deduction). */
  totalCapacityHours: number;
  /** Effective capacity after subtracting APPROVED time-off for the period. */
  effectiveCapacityHours: number;
  /** Time-off summary for PENDING and APPROVED statuses only */
  timeOffSummary: TimeOffSummaryItem[];
  /** Full time-off request details for PENDING and APPROVED requests in the period */
  timeOffRequests: TimeOffRequest[];
  progressPercentage: number;
  projectSummaries: ProjectTimeTrackingSummary[];
  bookedBreakdown: BookedMinutesBreakdown[];
}

export interface ProjectBreakdown {
  projectId: number;
  projectName: string;
  colorHex: string;
  logoUrl: string | null;
  trackedHours: number;
  trackedBreakdown: TrackedBreakdown[];
  bookedHours: number; // includes background work
  bookedBreakdown: BookedMinutesBreakdown[];
}

/** Extends ProjectBreakdown with a per-day background-work flag used only in the week view grid. */
export interface WeekDayProjectBreakdown extends ProjectBreakdown {
  /** True when all booked assignments for this project on this day are background work. */
  isBackgroundWork: boolean;
}

export interface WeekDayBreakdown {
  date: string; // ISO format (start-of-day UTC)
  /** Effective capacity after subtracting APPROVED time-off for the day. */
  dailyCapacityHours: number;
  /** Raw working capacity for the day, before any time-off deduction. `0` on weekends. */
  dailyTotalCapacityHours: number;
  trackedHours: number;
  trackedBreakdown: TrackedBreakdown[];
  bookedHours: number; // includes background work
  bookedBreakdown: BookedMinutesBreakdown[];
  projectBreakdown: WeekDayProjectBreakdown[];
  timeOff: TimeOffSummaryItem[];
}

export interface MonthCalendarDayTopProject {
  projectId: number;
  colorHex: string;
}

/** Per-day calendar entry for the month view's mini calendar. */
export interface MonthCalendarDay {
  date: string; // ISO
  trackedHours: number;
  /** Raw working capacity for the day, before time-off deduction. `0` on weekends. */
  totalCapacityHours: number;
  /** Capacity after deducting approved time-off for the day. */
  effectiveCapacityHours: number;
  topProjects: MonthCalendarDayTopProject[];
  /** time-off entries for this day (one per status). Empty when there is no time-off. */
  timeOff: TimeOffSummaryItem[];
  /** True when the day's time-off covers the whole working day. Derived from sum vs totalCapacity. */
  isFullDayTimeOff: boolean;
}

export interface MonthOverview {
  monthStartDate: string; // ISO
  monthEndDate: string; // ISO
  days: MonthCalendarDay[];
  /** Aggregated per-project totals for the month. */
  monthlyProjects: ProjectTimeTrackingSummary[];
  /** Full time-off requests overlapping the month (days trimmed to range). */
  timeOffRequests: TimeOffRequest[];
}

/** Detailed summary for a single day, used by the month view's right panel. */
export interface DayOverview {
  date: string; // ISO
  trackedHours: number;
  trackedBreakdown: TrackedBreakdown[];
  bookedHours: number; // includes background work
  bookedBreakdown: BookedMinutesBreakdown[];
  /** Raw daily capacity, before time-off deduction. `0` on weekends. */
  capacityHours: number;
  /** Capacity after deducting approved time-off for the day. */
  effectiveCapacityHours: number;
  timeOffApprovedHours: number;
  timeOffPendingHours: number;
  isFullDayTimeOff: boolean;
  timeOffStatus?: TimeOffRequestSummaryStatus;
  /** All time-off entries for the day. May contain PENDING and/or APPROVED items. */
  timeOff: TimeOffSummaryItem[];
  projectBreakdown: ProjectBreakdown[];
}

export enum ViewMode {
  Day = 'DAY',
  Week = 'WEEK',
  Month = 'MONTH',
}

export enum TimeTrackerView {
  Flat = 'flat',
  Cascade = 'cascade',
  Compact = 'compact',
}

export type CascadeSize = 'compact' | 'medium' | 'full';

export enum ProjectTimerState {
  /** Selected date is not today — timer controls are hidden entirely. */
  Unavailable = 'unavailable',
  /** No timer running — show the play button. */
  Idle = 'idle',
  /** Timer is running for this project — show pause/stop/trash controls. */
  ActiveHere = 'activeHere',
  /** Timer is running for a different project — show disabled play button. */
  ActiveElsewhere = 'activeElsewhere',
}

export type ProjectTimerControls =
  | { state: ProjectTimerState.Unavailable }
  | {
      state: ProjectTimerState.Idle;
      onPlay: (e: React.MouseEvent<HTMLButtonElement>) => void;
      isStartPending: boolean;
      isAnyActionPending: boolean;
    }
  | {
      state: ProjectTimerState.ActiveHere;
      isPaused: boolean;
      isLimitReached: boolean;
      isTogglePausePending: boolean;
      isStopPending: boolean;
      isDiscardPending: boolean;
      isAnyActionPending: boolean;
      onTogglePause: (e: React.MouseEvent<HTMLButtonElement>) => void;
      onStop: (e: React.MouseEvent<HTMLButtonElement>) => void;
      onReset: (e: React.MouseEvent<HTMLButtonElement>) => void;
    }
  | { state: ProjectTimerState.ActiveElsewhere };
